/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Accordion, AccordionContent, AccordionItem } from '@/components/ui/accordion';
import { ChevronDownIcon } from 'lucide-react';
import { getOrderSummaryMobileHeading, type OrderSummaryBasket } from './mobile-heading-utils';
import { formatCurrency } from '@/lib/currency';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';

interface OrderSummaryMobileAccordionProps {
    basket: OrderSummaryBasket;
    defaultExpanded?: boolean;
    // When true, displays the total price alongside the heading.
    showPrice?: boolean;
    // When true, the heading reads "Estimated Total" instead of "Total".
    isEstimate?: boolean;
    // Keep summary content as children so parent routes retain lazy-loading boundaries.
    children: ReactNode;
}

/**
 * Shared mobile wrapper for order-summary sections (cart + checkout).
 *
 * Responsibilities:
 * - Renders the mobile summary heading trigger with item-count text
 * - Optionally displays the estimated total price in the trigger (checkout)
 * - Manages collapsed/expanded accordion state
 * - Hosts caller-provided summary content in the accordion body
 *
 * Important design constraint:
 * - This component intentionally does NOT import `OrderSummary` directly.
 *   Parents pass summary content via `children` so route-level lazy-loading
 *   and code-splitting boundaries stay intact (especially on checkout).
 */
export function OrderSummaryMobileAccordion({
    basket,
    // Collapsed by default on mobile unless a caller opts in.
    defaultExpanded = false,
    showPrice = false,
    isEstimate = true,
    children,
}: OrderSummaryMobileAccordionProps): ReactElement {
    const { t, i18n } = useTranslation('cart');
    const { currency } = useSite();

    return (
        <Accordion
            type="single"
            collapsible
            defaultValue={defaultExpanded ? 'order-summary' : undefined}
            className="w-full border-b border-[#D8DAE0]">
            <AccordionItem value="order-summary" className="border-b-0">
                <AccordionPrimitive.Header className="flex w-full">
                    <AccordionPrimitive.Trigger
                        data-slot="accordion-trigger"
                        className="flex flex-1 items-center justify-between px-[var(--cart-summary-px)] py-4 hover:no-underline group w-full text-left font-semibold text-primary transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&[data-state=open]_.accordion-chevron]:rotate-180">
                        <span className="flex items-center gap-1.5 text-base">
                            <ChevronDownIcon className="accordion-chevron text-muted-foreground pointer-events-none size-4 shrink-0 transition-transform duration-200" />
                            <span>{getOrderSummaryMobileHeading(t, basket, isEstimate)}</span>
                        </span>
                        {showPrice && (
                            <span className="text-base shrink-0 ml-2 font-semibold">
                                {formatCurrency(
                                    basket?.orderTotal ?? basket?.productTotal ?? 0,
                                    i18n.language,
                                    currency
                                )}
                            </span>
                        )}
                    </AccordionPrimitive.Trigger>
                </AccordionPrimitive.Header>
                <AccordionContent className="p-0 pt-4 border-t border-[#D8DAE0]">{children}</AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
